<script setup lang="ts">
import { computed } from 'vue'
import {
  AdminDataTable,
  AdminMetricStrip,
  AdminPageHeader,
  AdminStatusTag,
  type AdminMetricItem
} from '@xjlv2/admin-layout'
import type { ApiPermissionRow, DataScopeRow, Dept, PermissionGroup, PermissionRow } from '../../types'

const props = defineProps<{
  permissions: PermissionRow[]
  groups: PermissionGroup[]
  apis: ApiPermissionRow[]
  dataScopes: DataScopeRow[]
  depts: Dept[]
}>()

const metrics = computed<AdminMetricItem[]>(() => [
  {
    key: 'granted',
    label: '当前有效权限',
    value: props.permissions.filter((permission) => permission.granted).length,
    hint: `共 ${props.permissions.length} 个权限点`,
    tone: 'primary'
  },
  {
    key: 'apis',
    label: 'API 映射',
    value: props.apis.length,
    hint: `${props.apis.filter((api) => api.granted).length} 个当前允许`,
    tone: 'success'
  },
  {
    key: 'resources',
    label: '数据资源',
    value: props.dataScopes.length,
    hint: '按租户与部门隔离'
  },
  {
    key: 'departments',
    label: '组织部门',
    value: props.depts.length,
    hint: '用于角色数据范围'
  }
])

function deptName(deptId: number) {
  return props.depts.find((dept) => dept.id === deptId)?.name ?? String(deptId)
}
</script>

<template>
  <section class="permission-overview">
    <AdminPageHeader
      eyebrow="Access Control"
      title="权限策略总览"
      description="核对当前管理员的功能权限、接口访问能力和数据范围；角色与管理员授权请从左侧相应菜单进入。"
      :meta="`${groups.length} 个权限域`"
    />

    <AdminMetricStrip :metrics="metrics" />

    <section class="permission-overview__grid">
      <article class="panel permission-overview__catalog">
        <div class="panel-head">
          <div>
            <h2>功能权限点</h2>
            <small>按来源模块归组</small>
          </div>
          <span>{{ permissions.filter((permission) => permission.granted).length }}/{{ permissions.length }}</span>
        </div>
        <div class="permission-overview__groups">
          <section v-for="group in groups" :key="group.source" class="permission-overview__group">
            <header>
              <strong>{{ group.source }}</strong>
              <span>{{ group.permissions.filter((permission) => permission.granted).length }}/{{ group.permissions.length }}</span>
            </header>
            <div v-for="permission in group.permissions" :key="permission.code" class="permission-overview__permission">
              <span>
                <strong>{{ permission.code }}</strong>
                <small>{{ permission.source }}</small>
              </span>
              <AdminStatusTag :tone="permission.granted ? 'success' : 'neutral'">
                {{ permission.granted ? '已授权' : '未授权' }}
              </AdminStatusTag>
            </div>
          </section>
          <p v-if="permissions.length === 0" class="empty-state">当前账号没有权限目录查看权限。</p>
        </div>
      </article>

      <AdminDataTable
        title="API 权限映射"
        description="接口访问由权限标识和当前角色共同决定。"
        :count="apis.length"
        :min-width="560"
        :empty="apis.length === 0"
        empty-title="暂无 API 权限映射"
      >
        <template #head>
          <tr>
            <th>方法</th>
            <th>接口路径</th>
            <th>权限标识</th>
            <th>结果</th>
          </tr>
        </template>
        <template #body>
          <tr v-for="api in apis" :key="`${api.method}:${api.path}`">
            <td><code class="permission-overview__method">{{ api.method }}</code></td>
            <td><code>{{ api.path }}</code></td>
            <td><span class="permission-overview__permission-code">{{ api.permission }}</span></td>
            <td>
              <AdminStatusTag :tone="api.granted ? 'success' : 'danger'">
                {{ api.granted ? '允许' : '拒绝' }}
              </AdminStatusTag>
            </td>
          </tr>
        </template>
      </AdminDataTable>

      <AdminDataTable
        class="permission-overview__resources"
        title="数据权限资源"
        description="数据范围在 tenant_id 基础上继续按本人、部门、部门树或自定义部门收敛。"
        :count="dataScopes.length"
        :min-width="760"
        :empty="dataScopes.length === 0"
        empty-title="暂无数据权限资源"
      >
        <template #head>
          <tr>
            <th>资源编码</th>
            <th>数据表</th>
            <th>租户字段</th>
            <th>已配置范围</th>
          </tr>
        </template>
        <template #body>
          <tr v-for="resource in dataScopes" :key="resource.code">
            <td><strong>{{ resource.code }}</strong></td>
            <td><code>{{ resource.tableName }}</code></td>
            <td><code>{{ resource.tenantColumn }}</code></td>
            <td>
              <div class="permission-overview__scopes">
                <AdminStatusTag
                  v-for="grant in resource.grants"
                  :key="`${resource.code}:${grant.scopeType}`"
                  tone="primary"
                  :dot="false"
                >
                  {{ grant.scopeType }}
                  <template v-if="grant.customDeptIds.length">
                    · {{ grant.customDeptIds.map(deptName).join('、') }}
                  </template>
                </AdminStatusTag>
                <AdminStatusTag v-if="resource.grants.length === 0" :dot="false">未配置</AdminStatusTag>
              </div>
            </td>
          </tr>
        </template>
      </AdminDataTable>
    </section>
  </section>
</template>

<style scoped>
.permission-overview {
  display: grid;
  gap: 16px;
}

.permission-overview__grid {
  display: grid;
  grid-template-columns: minmax(320px, 0.85fr) minmax(540px, 1.45fr);
  align-items: start;
  gap: 16px;
}

.permission-overview__catalog {
  min-width: 0;
  max-height: 640px;
  overflow: hidden;
  padding: 0;
}

.permission-overview__catalog .panel-head {
  margin: 0;
  border-bottom: 1px solid var(--rr-color-border);
  padding: 12px 14px;
}

.permission-overview__catalog .panel-head > div {
  display: grid;
  gap: 3px;
}

.permission-overview__groups {
  max-height: 580px;
  overflow: auto;
  padding: 8px;
}

.permission-overview__group + .permission-overview__group {
  border-top: 1px solid var(--rr-color-border);
  margin-top: 8px;
  padding-top: 8px;
}

.permission-overview__group > header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: var(--rr-color-text-muted);
  font-size: 11px;
  padding: 6px 8px;
}

.permission-overview__permission {
  display: flex;
  min-height: 47px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-radius: var(--rr-radius-control);
  padding: 5px 8px;
}

.permission-overview__permission:hover {
  background: var(--rr-color-row-hover);
}

.permission-overview__permission > span {
  display: grid;
  min-width: 0;
  gap: 3px;
}

.permission-overview__permission strong,
.permission-overview__permission small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.permission-overview__permission strong {
  color: var(--rr-color-text);
  font-family: "SFMono-Regular", Consolas, monospace;
  font-size: 11px;
}

.permission-overview__permission small {
  color: var(--rr-color-text-subtle);
  font-size: 10px;
}

.permission-overview__resources {
  grid-column: 1 / -1;
}

.permission-overview__method {
  color: var(--rr-color-primary);
  font-weight: 760;
}

.permission-overview__permission-code {
  color: var(--rr-color-text-muted);
  font-family: "SFMono-Regular", Consolas, monospace;
  font-size: 11px;
}

.permission-overview__scopes {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

@media (max-width: 1080px) {
  .permission-overview__grid {
    grid-template-columns: 1fr;
  }

  .permission-overview__resources {
    grid-column: auto;
  }
}
</style>
